// The named terminal-refusal screen and its Retry (sdk ADR-013).
import type { BootRefusalCode, LaunchRefusalCode } from '@localagent/sdk';

/** Every terminal cause a boot can end on, its own and launch's (sdk ADR-016). */
export type BootFailureCode = BootRefusalCode | LaunchRefusalCode;

const BOOT_REFUSALS: Record<BootFailureCode, { title: string; body: string }> = {
  'daemon-missing': {
    title: "The ShogunChop service isn't running",
    body: 'This app needs its background service to start. Click Retry to start it.',
  },
  'snapshot-unavailable': {
    title: "Can't reach the background service",
    body: "The service did not report its sign-in state in time, so the app cannot tell whether you're signed in. Retry once it responds.",
  },
  'convergence-bundle-missing': {
    title: 'ShogunChop is not installed where it expects to be',
    body: 'The app is running from outside its application bundle, so its background services cannot be registered. Move ShogunChop.app into Applications and retry.',
  },
  'convergence-registrar-missing': {
    title: 'This ShogunChop install is incomplete',
    body: 'The installer script that registers the background services is missing from the app bundle. Reinstall ShogunChop, then retry.',
  },
  'convergence-script-failed': {
    title: "ShogunChop's background services could not be registered",
    body: 'Registering the daemon and menubar services failed. Retry — if it keeps failing, check the launch log in ~/.localagent.',
  },
  'convergence-daemon-silent': {
    title: 'The background service was registered but never started',
    body: 'The services are registered and macOS was asked to start them, but none reported in. Retry to run the registration again.',
  },
  'convergence-tray-silent': {
    title: 'The menu bar app did not start',
    body: 'The background service is up, but the menu bar app never reported in. Retry to start it again.',
  },
};

export interface BootRefusalScreenProps {
  code: BootFailureCode;
  onRetry: () => void;
}

export function BootRefusalScreen({ code, onRetry }: BootRefusalScreenProps) {
  const refusal = BOOT_REFUSALS[code];
  return (
    <div className="flex h-full min-h-full items-center justify-center bg-background">
      <div
        data-testid="boot-refusal"
        data-boot-refusal={code}
        className="max-w-md rounded-lg bg-warning-subtle p-6 text-center text-body text-warning-text"
      >
        <div className="mb-2 text-title-2 font-semibold">{refusal.title}</div>
        <p className="mb-4">{refusal.body}</p>
        <button
          type="button"
          data-testid="boot-refusal-retry"
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    </div>
  );
}
